<template>
  <div class="flex flex-col w-full">
    <div v-if="hasHeader" class="mb-base">
      <slot name="header" />
    </div>
    <div v-if="hasText" class="mb-base">
      <slot name="text" />
    </div>

    <SharingTypeSelection
      v-model="sharingType"
      :visibility="typeVisibility"
    />

    <div v-if="sharingType === 'individuals'" data-test="select-individuals">
      <MultiSelect
        v-if="allowSharingWithIndividuals"
        v-model="selectedActors"
        @select="addItem"
        @remove="removeItem"
        @search-change="handleSearch"
        :placeholder="$t('sharing.inputPlaceholder')"
        :options="availableActors"
        :internal-search="false"
        :loading="isSearchingUsers"
        label="name"
        track-by="id"
      >
        <template v-slot:tag="props">
          <span class="multiselect__tag">
            <span>{{ props.option.name }}</span>
            <i aria-hidden="true" tabindex="0" class="multiselect__tag-icon" @keypress.enter.prevent="props.remove(props.option)" @click="props.remove(props.option)"></i>
          </span>
        </template>
        <template v-slot:option="props">
          <div class="grid grid-cols-2">
            <span class="col-span-1 mr-2 truncate">
              {{ props.option.name }}
            </span>
            <span class="col-span-1 text-14 font-normal truncate">
              <template v-if="props.option.type==='user'">
                {{ props.option.actor.email }}
              </template>
              <template v-if="props.option.type==='group'">
                {{ $t('groups.members') }} {{ props.option.actor.users_count }}
              </template>
            </span>
          </div>
        </template>
        <template v-slot:afterList v-if="moreResults">
          <li class="multiselect__element">
            <span class="multiselect__option text-center cursor-default">
              Refine search for more {{ moreResults }}...
            </span>
          </li>
        </template>
      </MultiSelect>
      <p v-if="selectedActors.length > 0" class="my-base permitted-actors-title">{{ permittedActorsListTitle ? permittedActorsListTitle : $t('sharing.permittedActorsListTitle') }}</p>
      <Transition>
        <div
          v-if="isLoading"
          data-test="list-item-placeholder"
          class="flex items-center justify-between my-base placeholder-border py-lg">
          <div class="inline-block animate-pulse bg-border mr-base rounded w-xl h-icon"></div>
          <div class="flex items-center pr-xs">
            <div class="inline-block animate-pulse bg-border mr-base rounded w-planCardHero h-icon"></div>
            <XMarkIcon class="w-lg animate-pulse fill-border h-lg mr-base ml-sm" />
          </div>
        </div>
        <ul v-else class="pl-base w-full overflow-y-auto max-h-envelopeSharingHeight mt-none">
          <ListItem
            data-test="list-item"
            v-for="(item, key) in selectedActors"
            :key="key"
            @remove="removeItem(item, key)"
          >
              <template v-slot:name>
              <span>{{ item.name }}</span>
              <span
                v-if="item.type === 'user' && item.actor.id === authenticatedUser.id"
                class="ml-base">
                {{ $t('sharing.you') }}
              </span>
            </template>
            <template v-slot:extraContent>
              <ElSelect
                v-model="item.permission"
                size="small"
                value-key="id"
                @change="$emit('permissionChanged', $event)"
              >
                <ElOption
                  v-for="(permission, key) in permissions"
                  :key="key"
                  :label="permission.name"
                  :value="permission"
                />
              </ElSelect>
            </template>
          </ListItem>
        </ul>
      </Transition>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { orderBy, cloneDeep } from 'lodash';
import MultiSelect from '@/components/MultiSelect.vue';
import ListItem from '@/components/tailwind/ListItem.vue';
import SharingTypeSelection from '@/views/components/SharingTypeSelection.vue';
import SearcherMixin from '@/mixins/searcherMixin';
import { XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'SharingView',
  components: {
    SharingTypeSelection,
    MultiSelect,
    ListItem,
    XMarkIcon,
  },
  props: {
    typeVisibility: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    permittedActorsListTitle: {
      type: String,
      required: false,
    },
    resource: {
      type: Object,
      required: false,
    },
    value: {
      type: Object,
      required: true,
    },
  },

  async mounted() {
    this.isLoading = true;
    await this.fetchData();
    this.initSelectedActors();
    this.initSharingType();
    this.isLoading = false;
  },

  mixins: [SearcherMixin('users'), SearcherMixin('groups')],

  data() {
    return {
      isLoading: false,
      selectedActors: [],
      selectedActorsHistory: undefined,
      sharingType: undefined,
    };
  },
  computed: {
    ...mapGetters({
      permissions: 'permissions/permissions',
      editorPermission: 'permissions/editor',
      authenticatedUser: 'user/user',
      planHasFeature: 'plan/planHasFeature',
    }),
    moreResults() {
      const more = [];
      if (!this.usersSearchResultsComplete) {
        more.push('users');
      }
      if (!this.groupsSearchResultsComplete) {
        more.push('teams');
      }
      return more.join(' and ');
    },
    availableActors() {
      const users = this.usersSearchResults.map(user => this.wrapInInterface(
          user.name,
          user,
          this.editorPermission,
          'user',
        ));

      const groups = this.groupsSearchResults.map(group => this.wrapInInterface(
          group.name,
          group,
          this.editorPermission,
          'group',
        ));

      return orderBy([...users, ...groups], [actor => actor.name.toLowerCase()], ['asc']);
    },
    allowSharingWithIndividuals() {
      return this.planHasFeature('team');
    },
    sharingWithEveryone() {
      return this.sharingType === 'everyone';
    },
    everyonePermission() {
      return this.sharingWithEveryone ? this.editorPermission : null;
    },
    hasHeader() { return !!this.$slots.header; },
    hasText() { return !!this.$slots.text; },
  },
  methods: {
    ...mapActions({
      getPermissions: 'permissions/GET',
    }),
    initSelectedActors() {
      const sharedUsers = this.value.users.map(resourcePermission => this.wrapInInterface(
        resourcePermission.user.name,
        resourcePermission.user,
        resourcePermission.permission,
        'user',
      ));

      const sharedGroups = this.value.groups.map(resourcePermission => this.wrapInInterface(
        resourcePermission.group.name,
        resourcePermission.group,
        resourcePermission.permission,
        'group',
      ));

      this.selectedActors = [...this.selectedActors, ...sharedUsers, ...sharedGroups];
    },
    async fetchData() {
      const promises = [
        // search methods provided by searcher mixins
        this.searchUsers(),
        this.searchGroups(),
      ];
      if (this.permissions?.length === 0) {
        promises.push(this.getPermissions());
      }
      await Promise.all(promises);
    },
    async handleSearch(query) {
      await Promise.all([
        // search methods provided by searcher mixins
        this.searchUsers({ query }),
        this.searchGroups({ query }),
      ]);
    },
    addItem(item) {
      this.$emit('itemAdded', item);
    },
    removeItem(item, removeIndex = -1) {
      if (removeIndex > -1) {
        this.selectedActors.splice(removeIndex, 1);
      }
      this.$emit('itemRemoved', item);
    },
    generateSettings() {
      const data = {
        everyone_permission_id: this.sharingWithEveryone ? this.everyonePermission?.id : null,
        everyone_permission: this.sharingWithEveryone ? this.everyonePermission : null,
        groups: [],
        users: [],
        actors: {
          groups: [],
          users: [],
        },
      };

      this.selectedActors.forEach((resourcePermission) => {
        if (resourcePermission.type === 'user') {
          data.users.push({
            permission_id: resourcePermission.permission.id,
            user_id: resourcePermission.actor.id,
          });
          data.actors.users.push({
            permission: resourcePermission.permission,
            user: resourcePermission.actor,
          });
        } else if (resourcePermission.type === 'group') {
          data.groups.push({
            permission_id: resourcePermission.permission.id,
            group_id: resourcePermission.actor.id,
          });
          data.actors.groups.push({
            permission: resourcePermission.permission,
            group: resourcePermission.actor,
          });
        }
      });

      return data;
    },
    initSharingType() {
      if (this.value.everyone_permission && this.typeVisibility.everyone !== false) {
        this.sharingType = 'everyone';
        return;
      }

      if (
        this.typeVisibility.private !== false &&
        this.selectedActors.length === 1 &&
        this.selectedActors[0].type === 'user' &&
        this.selectedActors[0].id === `user-${this.authenticatedUser.id}`
      ) {
        this.sharingType = 'private';
        return;
      }

      if (this.typeVisibility.individuals !== false) {
        this.sharingType = 'individuals';
      }
    },
    addAuthenticatedUserToSelectedActors() {
      this.selectedActors = [this.wrapInInterface(
        this.authenticatedUser.name,
        this.authenticatedUser,
        this.editorPermission,
        'user',
      )];
    },
    wrapInInterface(name, actor, permission, type, disabled = false) {
      return {
        name,
        actor,
        permission,
        type,
        id: `${type}-${actor.id}`,
        $isDisabled: disabled,
      };
    },

  },
  watch: {
    isLoading(newValue) {
      this.$emit('isLoading', newValue);
    },
    selectedActors: {
      deep: true,
      handler() {
        this.$emit('input', this.generateSettings());
      },
    },
    sharingType(newVal, oldVal) {
      // On the first set of sharingType we don't want to do anything
      if (!oldVal) {
        return;
      }

      if (oldVal === 'individuals') {
        this.selectedActorsHistory = cloneDeep(this.selectedActors);
      }

      if (newVal === 'individuals') {
        if (this.selectedActorsHistory) {
          this.selectedActors = cloneDeep(this.selectedActorsHistory);
        } else {
          // default to current user
          this.addAuthenticatedUserToSelectedActors();
        }
      } else if (newVal === 'everyone') {
        this.selectedActors = [];
      } else if (newVal === 'private') {
        this.addAuthenticatedUserToSelectedActors();
      }
    },
  },


};
</script>

<style>
/* TODO cant get tailwind v2 to work with border top and border bottom - remove after upgrading to v3 */
.placeholder-border {
  border-bottom: 1px solid #dde2e5;
  border-top: 1px solid #dde2e5;
}
/* TODO tailwind should provide this class - remove after upgrade */
.cursor-default {
  cursor: default;
}
</style>
